import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Paper, Typography, Box, Grid, Card, CardContent, CardActions,
  Button, Chip, LinearProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, List, ListItem, ListItemIcon, ListItemText, Alert,
  Radio, RadioGroup, FormControlLabel, FormControl, FormLabel,
  Divider
} from '@mui/material';
import {
  CheckCircle, Phone
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006/api';

const planFeatures = {
  free: ['1 active test', '20 candidates/month', 'Basic reporting', 'Email support'],
  basic: ['5 active tests', '50 candidates/month', 'MCQ only', 'Standard reporting', 'Email + chat support'],
  professional: ['25 active tests', '500 candidates/month', 'MCQ + open-ended', 'Advanced analytics', 'Priority support'],
  ultimate: ['Unlimited tests', '1,500 candidates/month', 'Custom branding', 'API access', '24/7 support'],
  enterprise: ['Unlimited everything', 'Dedicated server option', 'SSO/SAML integration', 'On-premise option', 'Account manager']
};

const SubscriptionManager = () => {
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [processing, setProcessing] = useState(false);
  const [pendingTx, setPendingTx] = useState(null);

  const getToken = () => localStorage.getItem('adminToken');
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [subRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/payments/subscription`, headers),
        axios.get(`${API_URL}/payments/plans`)
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChangePlan = (plan) => {
    setSelectedPlan(plan);
    setPaymentMethod('');
    setPhoneNumber('');
    setChangeDialogOpen(true);
  };

  const handleConfirmChange = async () => {
    if (!selectedPlan) return;

    // Free plan - instant switch
    if (selectedPlan.price === 0) {
      setProcessing(true);
      try {
        await axios.post(`${API_URL}/payments/subscription/change`,
          { planSlug: selectedPlan.slug }, authHeaders());
        toast.success(`Switched to ${selectedPlan.name} plan!`);
        setChangeDialogOpen(false);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to change plan');
      } finally {
        setProcessing(false);
      }
      return;
    }

    // Paid plans - need payment method
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    if (paymentMethod === 'pesapal' && !phoneNumber.trim()) {
      toast.error('Phone number required for Mobile Money');
      return;
    }

    setProcessing(true);
    try {
      if (paymentMethod === 'mtn_momo') {
        const res = await axios.post(`${API_URL}/payments/mtn-momo/initiate`,
          { planSlug: selectedPlan.slug }, authHeaders());
        toast.success(res.data.message || 'Payment initiated!');
        // Show pending transaction details
        if (res.data.transaction_id) {
          setPendingTx(res.data);
        }
      } else if (paymentMethod === 'pesapal') {
        const res = await axios.post(`${API_URL}/payments/pesapal/initiate`,
          { planSlug: selectedPlan.slug, phoneNumber }, authHeaders());
        // Redirect to PesaPal payment page
        if (res.data.redirect_url) {
          window.location.href = res.data.redirect_url;
        }
      } else if (paymentMethod === 'paypal') {
        const res = await axios.post(`${API_URL}/payments/paypal/initiate`,
          { planSlug: selectedPlan.slug }, authHeaders());
        if (res.data.approval_url) {
          window.location.href = res.data.approval_url;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure? Your subscription will be cancelled and you will revert to the Free plan.')) return;
    try {
      await axios.post(`${API_URL}/payments/subscription/cancel`, {}, authHeaders());
      toast.success('Subscription cancelled');
      fetchData();
    } catch (err) {
      toast.error('Failed to cancel subscription');
    }
  };

  const currentPlanSlug = subscription?.plan_slug || 'free';

  if (loading) return <LinearProgress />;

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Subscription & Billing</Typography>

      {subscription && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Current Plan:</strong> {subscription.plan_name} ({subscription.status})
          {subscription.current_period_end && (
            <> — Renews: {new Date(subscription.current_period_end).toLocaleDateString()}</>
          )}
          {subscription.plan_slug !== 'free' && subscription.status === 'active' && (
            <Button size="small" sx={{ ml: 2 }} color="error" onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </Alert>
      )}

      <Grid container spacing={3}>
        {plans.map((plan) => {
          const isCurrent = plan.slug === currentPlanSlug;
          const isPopular = plan.slug === 'professional';
          const features = planFeatures[plan.slug] || [];

          return (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Card sx={{
                height: '100%', display: 'flex', flexDirection: 'column',
                border: isCurrent ? '2px solid' : '1px solid',
                borderColor: isCurrent ? 'primary.main' : 'grey.200',
                position: 'relative'
              }}>
                {isPopular && (
                  <Chip label="Most Popular" color="primary" size="small"
                    sx={{ position: 'absolute', top: -10, right: 16 }} />
                )}
                {isCurrent && (
                  <Chip label="Current" variant="outlined" color="success" size="small"
                    sx={{ position: 'absolute', top: -10, left: 16 }} />
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" gutterBottom>{plan.name}</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h4" component="span" color="primary">
                      ${plan.price}
                    </Typography>
                    <Typography variant="body2" component="span" color="text.secondary">
                      /month
                    </Typography>
                  </Box>

                  <List dense>
                    {features.map((f, i) => (
                      <ListItem key={i} sx={{ py: 0.3 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={f} />
                      </ListItem>
                    ))}
                    {plan.api_access && (
                      <ListItem sx={{ py: 0.3 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="API access" />
                      </ListItem>
                    )}
                    {plan.sso_enabled && (
                      <ListItem sx={{ py: 0.3 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="SSO/SAML" />
                      </ListItem>
                    )}
                  </List>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={isCurrent ? 'outlined' : 'contained'}
                    disabled={isCurrent}
                    onClick={() => handleChangePlan(plan)}
                  >
                    {isCurrent ? 'Current Plan' : plan.price === 0 ? 'Downgrade to Free' : 'Upgrade'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Change Plan Dialog */}
      <Dialog open={changeDialogOpen} onClose={() => setChangeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedPlan?.price === 0 ? 'Switch to Free Plan' : `Upgrade to ${selectedPlan?.name}`}
        </DialogTitle>
        <DialogContent dividers>
          {selectedPlan && (
            <>
              <Typography variant="h6" color="primary" gutterBottom>
                ${selectedPlan.price}/month — {selectedPlan.name}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {selectedPlan.price > 0 && (
                <>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel>Payment Method</FormLabel>
                    <RadioGroup value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                      <FormControlLabel
                        value="mtn_momo"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone fontSize="small" color="primary" /> MTN MoMo (Manual) — Rwanda/EAC
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>

                  {paymentMethod === 'mtn_momo' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        📱 MTN MoMo Manual Payment
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Send <strong>${selectedPlan.price} USD</strong> via MTN Mobile Money to:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                        +250 788 620 201
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        After sending the payment, click "Initiate Payment" to create a pending
                        transaction, then confirm once the transfer is complete.
                      </Typography>
                    </Alert>
                  )}
                </>
              )}

              {selectedPlan.price === 0 && (
                <Alert severity="warning">
                  You are switching to the Free plan. Some features will be limited.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmChange} disabled={processing}>
            {processing ? 'Processing...' : selectedPlan?.price === 0 ? 'Confirm Downgrade' : 'Proceed to Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MTN MoMo Confirmation Dialog */}
      <Dialog open={!!pendingTx} onClose={() => setPendingTx(null)} maxWidth="sm" fullWidth>
        <DialogTitle>📱 Confirm MTN MoMo Payment</DialogTitle>
        <DialogContent dividers>
          {pendingTx && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Send <strong>${pendingTx.amount} {pendingTx.currency}</strong> to:
              </Alert>

              <Box sx={{ textAlign: 'center', mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                  {pendingTx.merchant_phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Merchant: {pendingTx.merchant_name}
                </Typography>
              </Box>

              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  ✅ After sending the payment, click the button below to confirm.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Transaction ID: <code>{pendingTx.transaction_id}</code>
                </Typography>
              </Alert>

              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                onClick={async () => {
                  try {
                    setProcessing(true);
                    const res = await axios.post(`${API_URL}/payments/mtn-momo/confirm`,
                      { transactionId: pendingTx.transaction_id }, authHeaders());
                    toast.success(res.data.message || 'Payment confirmed!');
                    setPendingTx(null);
                    setChangeDialogOpen(false);
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.error || 'Failed to confirm payment');
                  } finally {
                    setProcessing(false);
                  }
                }}
                disabled={processing}
                sx={{ mb: 2 }}
              >
                {processing ? 'Confirming...' : '✅ I Have Sent the Payment — Confirm'}
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTx(null)} color="error">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SubscriptionManager;
